import { parsePpeResponse, ppeAuthHeaders, ppePricingUrl } from "../../config";
import { formatPpeSearchParams } from "../../pricingRequest";
import { incomeDocumentationName } from "../search/searchFields";
import { termLabel } from "./resultModel";

// Change suggestions on a search where nothing is eligible (plan
// ppe-simplify-epps.md, D18, section 6.8, work package 8). The calculator's
// view=alternatives prices the search plus up to 8 one-step changes -- another
// income documentation type, one more loan term, Agency instead of Non-QM (or
// the reverse), no income -- and returns how many eligible products each
// gives. The page shows the best few after the zero-result sentence; clicking
// one changes the form and searches again.

// How many suggestions the page shows.
export const MAX_SHOWN_ALTERNATIVES = 4;

const LOAN_TERM_KEYS = ["loanTerm", "amortizationType", "armFixedTerm"];

// The request body for the whole search: every ticked term in one request
// (`loanTerms`, work package 2), so the calculator can try adding a term.
export function alternativesBody(formBody, terms) {
  const list = terms && terms.length ? terms : [formBody.loanTerm];
  const body = formatPpeSearchParams({ ...formBody, selectedLoanTerms: [list[0]], loanTerm: list[0] }).pricingRequest;
  if (list.length > 1) {
    LOAN_TERM_KEYS.forEach((key) => delete body[key]);
    body.loanTerms = list;
  }
  return body;
}

export function fetchAlternatives(formBody, terms, fetchImpl = fetch) {
  return fetchImpl(ppePricingUrl("alternatives"), {
    method: "POST",
    headers: ppeAuthHeaders(),
    body: JSON.stringify(alternativesBody(formBody, terms)),
  }).then(parsePpeResponse);
}

// The button text, e.g. "Income doc: Bank statements · Business · 12 months
// +13": the same whole name the Income documentation box shows.
export function alternativeLabel(alt) {
  const added = `+${alt.addedProducts}`;
  switch (alt.kind) {
    case "incomeDocumentation":
      return `Income doc: ${incomeDocumentationName(alt.value)} ${added}`;
    case "addLoanTerm":
      return `Add ${termLabel(alt.value)} ${added}`;
    case "loanProgram":
      return `${alt.value === "conventional" ? "Agency (Full Doc)" : "Non-QM"} ${added}`;
    case "clearIncome":
      return `Clear income ${added}`;
    default:
      return `${alt.label} ${added}`;
  }
}

// The form body with one suggestion applied, in the shape the form restores
// from and formatPpeSearchParams reads.
export function applyAlternative(formBody, alt, terms) {
  const body = { ...formBody };
  const current = terms && terms.length ? terms : [formBody.loanTerm];
  switch (alt.kind) {
    case "incomeDocumentation": {
      const nonQm = { ...(formBody.nonQm || {}), incomeDocumentation: [alt.value] };
      delete nonQm.dscrMode;
      delete nonQm.dscr;
      body.nonQm = nonQm;
      break;
    }
    case "addLoanTerm":
      body.selectedLoanTerms = [...current, alt.value];
      body.loanTerm = current[0];
      break;
    case "loanProgram":
      body.loanProgram = alt.value;
      if (alt.value === "conventional") {
        delete body.nonQm;
      } else if (!body.nonQm) {
        body.nonQm = { incomeDocumentation: ["FullDoc"] };
      }
      break;
    case "clearIncome":
      body.monthlyIncome = undefined;
      body.monthlyDebt = undefined;
      body.dti = undefined;
      break;
    default:
      break;
  }
  return body;
}

// The suggestions to show: the calculator already dropped changes that add
// nothing and sorted the rest, most products first.
export function shownAlternatives(response) {
  const list = (response && response.alternatives) || [];
  return list.filter((a) => a.addedProducts > 0).slice(0, MAX_SHOWN_ALTERNATIVES);
}
